import teamService from "../../../services/api/teamService";

/**
 * Custom hook for team member-related actions
 */
export const useTeamActions = (teamId, team, currentUser, userMembership, fetchTeamData) => {
  const handleJoinTeam = async (setError, setSuccessMessage) => {
    if (!currentUser) {
      setError("You must be logged in to join a team.");
      return null;
    }

    try {
      setError("");
      setSuccessMessage("");

      if (team.max_members) {
        const totalCapacity = (team.max_members || 0) + (team.max_substitutes || team.settings?.max_substitutes || 0);
        if (team.active_members_count >= totalCapacity) {
          setError(`Team is full (${team.active_members_count}/${totalCapacity} members)`);
          return null;
        }
      }

      const response = await teamService.joinTeam(teamId, {
        requestedRole: "player",
        message: "I would like to join your team",
      });

      if (response.success) {
        const status = response.data?.status || "pending";
        const message =
          status === "pending"
            ? "Join request sent! Waiting for team approval."
            : "Successfully joined the team!";
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(""), 5000);
        await fetchTeamData();
        return status;
      }
    } catch (err) {
      console.error("Error joining team:", err);
      let errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to send join request. Please try again.";

      // The server answers 409 with a clean message (no "CODE:" prefix) for
      // "already pending" / "already a member"; older servers used prefixes.
      const status = err.response?.status;
      if (errorMessage.startsWith("PENDING_REQUEST:") || (status === 409 && /pending/i.test(errorMessage))) {
        setError("");
        return "pending";
      } else if (errorMessage.startsWith("ALREADY_MEMBER:") || (status === 409 && /already/i.test(errorMessage))) {
        const cleanMessage = errorMessage.replace("ALREADY_MEMBER:", "");
        setError(cleanMessage);
        await fetchTeamData();
        return "active";
      } else {
        setError(errorMessage);
      }
    }
    return null;
  };

  const handleLeaveTeam = (setError) => {
    if (!currentUser || !userMembership) return false;

    // Ownership is teams.created_by (creators hold the 'captain' role, so the
    // old role === "manager" check never fired) (TM-B12).
    if (team?.created_by != null && Number(team.created_by) === Number(currentUser.id)) {
      setError("You own this team. Transfer ownership to another member (Members tab) or delete the team before leaving.");
      return false;
    }
    return true;
  };

  const confirmLeaveTeam = async (setError, setSuccessMessage, setShowLeaveConfirm, setIsLeaving) => {
    try {
      setError("");
      setSuccessMessage("");
      setShowLeaveConfirm(false);
      setIsLeaving(true);
      await teamService.leaveTeam(teamId);
      setSuccessMessage("You have left the team. You can rejoin anytime!");
      setTimeout(() => setSuccessMessage(""), 5000);
      await fetchTeamData();
    } catch (err) {
      console.error("Error leaving team:", err);
      setError("Failed to leave team. Please try again.");
    } finally {
      setIsLeaving(false);
    }
  };

  // Hand the team to another active member (TM-B12)
  const transferOwnership = async (member, setError, setSuccessMessage) => {
    const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.username || "this member";
    if (!window.confirm(`Make ${name} the owner of this team? You will no longer be able to manage roles or delete the team.`)) {
      return false;
    }
    try {
      setError("");
      await teamService.transferOwnership(teamId, member.user_id);
      setSuccessMessage(`${name} now owns the team.`);
      setTimeout(() => setSuccessMessage(""), 5000);
      await fetchTeamData();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to transfer ownership.");
      return false;
    }
  };

  const handleRejoinTeam = async (setError, setSuccessMessage) => {
    if (!currentUser) return;

    try {
      setError("");
      setSuccessMessage("");
      const response = await teamService.rejoinTeam(teamId);

      if (response.success) {
        setSuccessMessage(
          response.data?.status === "pending"
            ? "Your request to rejoin was sent and is waiting for approval."
            : "Welcome back to the team!"
        );
        setTimeout(() => setSuccessMessage(""), 5000);
        await fetchTeamData();
      }
    } catch (err) {
      console.error("Error rejoining team:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to rejoin team. Please try again.";
      setError(errorMessage);
    }
  };

  const handleApproveRequest = (userId, username, setApproveTarget, setSelectedMemberType, setShowApproveModal) => {
    setApproveTarget({ userId, username });
    // No preset squad: the server picks starter if there is room, else sub.
    setSelectedMemberType(null);
    setShowApproveModal(true);
  };

  const confirmApproveRequest = async (approveTarget, selectedMemberType, setError, setSuccessMessage, setShowApproveModal, setIsChangingMemberType) => {
    if (!approveTarget) return;
    try {
      setError("");
      setSuccessMessage("");
      setShowApproveModal(false);

      const result = await teamService.approveJoinRequest(teamId, approveTarget.userId, "Welcome to the team!", selectedMemberType);
      const placedAs = result?.data?.member_type || selectedMemberType;

      setSuccessMessage(
        placedAs
          ? `Approved ${approveTarget.username} to join as ${placedAs === "primary" ? "starter" : "substitute"}!`
          : `Approved ${approveTarget.username} to join the team!`
      );
      setTimeout(() => setSuccessMessage(""), 5000);
      await fetchTeamData();
    } catch (err) {
      console.error("Error approving request:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to approve request. Please try again.";
      setError(errorMessage);
    }
  };

  const handleRejectRequest = (userId, username, setRejectTarget, setShowRejectConfirm) => {
    setRejectTarget({ userId, username });
    setShowRejectConfirm(true);
  };

  const confirmRejectRequest = async (rejectTarget, setError, setSuccessMessage, setShowRejectConfirm, setRejectTarget2) => {
    if (!rejectTarget) return;
    try {
      setError("");
      setSuccessMessage("");
      setShowRejectConfirm(false);
      await teamService.rejectJoinRequest(teamId, rejectTarget.userId, "Sorry, we're not accepting new members at this time.");
      setSuccessMessage(`Rejected ${rejectTarget.username}'s request.`);
      setTimeout(() => setSuccessMessage(""), 5000);
      await fetchTeamData();
    } catch (err) {
      console.error("Error rejecting request:", err);
      const errorMessage = err.response?.data?.error || "Failed to reject request. Please try again.";
      setError(errorMessage);
    } finally {
      setRejectTarget2(null);
    }
  };

  const handleRemoveMember = (userId, userName, setRemoveMemberTarget, setShowRemoveMemberConfirm) => {
    setRemoveMemberTarget({ userId, userName });
    setShowRemoveMemberConfirm(true);
  };

  const confirmRemoveMember = async (removeMemberTarget, setError, setIsRemovingMember, setShowRemoveMemberConfirm, setSuccessMessage, setRemoveMemberTarget2) => {
    if (!removeMemberTarget) return;

    try {
      setError("");
      setIsRemovingMember(true);
      setShowRemoveMemberConfirm(false);
      await teamService.removeMember(teamId, removeMemberTarget.userId);
      await fetchTeamData();
      setSuccessMessage(`${removeMemberTarget.userName} has been removed from the team.`);
    } catch (err) {
      console.error("Error removing member:", err);
      const errorMessage = err.response?.data?.error || "Failed to remove member. Please try again.";
      setError(errorMessage);
    } finally {
      setIsRemovingMember(false);
      setRemoveMemberTarget2(null);
    }
  };

  return {
    transferOwnership,
    handleJoinTeam,
    handleLeaveTeam,
    confirmLeaveTeam,
    handleRejoinTeam,
    handleApproveRequest,
    confirmApproveRequest,
    handleRejectRequest,
    confirmRejectRequest,
    handleRemoveMember,
    confirmRemoveMember,
  };
};

export default useTeamActions;
